import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ChipItem } from '@wind-log_library/fe-component-library';
import { ColorStatusCodePipe } from 'app/shared/pipes/color-status/color-status.pipe';

type AlarmCategory = 'LIMITED_OPERATION' | 'ALARM_STOP' | 'IN_SERVICE' | 'NO_DATA';

const alarmCategoryStatusCodes: Record<AlarmCategory, number | null> = {
  LIMITED_OPERATION: 1,
  ALARM_STOP: 2,
  IN_SERVICE: 4,
  NO_DATA: null,
};

@Component({
  selector: 'app-performance-alarm-list',
  templateUrl: './performance-alarm-list.component.html',
  styleUrls: ['./performance-alarm-list.component.scss'],
  standalone: false,
})
export class PerformanceAlarmListComponent implements OnChanges {
  @Input() alarms: any[] = [];

  @Input() timezone: string;
  @Input() popupVersion = 0;
  @Input() expandOnOpen = false;
  @Output() clicked = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  public expanded = false;
  private readonly alarmCategoryChipDefinitions: ChipItem[] = [
    {
      id: 'LIMITED_OPERATION',
      label: 'ALARMS.LIMITED_OPERATION',
      selected: true,
      selectedColors: {
        light: '#F5E1F5',
        dark: '#633263',
      },
    },
    {
      id: 'ALARM_STOP',
      label: 'ALARMS.ALARM_STOP',
      selected: true,
      selectedColors: {
        light: '#FDE8E9',
        dark: '#B02A37',
      },
    },
    {
      id: 'IN_SERVICE',
      label: 'ALARMS.IN_SERVICE',
      selected: true,
      selectedColors: {
        light: '#CCECF8',
        dark: '#009FDA',
      },
    },
    {
      id: 'NO_DATA',
      label: 'ALARMS.NO_DATA',
      selected: true,
      selectedColors: {
        light: '#E9ECEF',
        dark: '#495057',
      },
    },
  ];

  alarmCategoryChips: ChipItem[] = [];

  private selectedCategories = new Set<AlarmCategory>(
    this.alarmCategoryChipDefinitions.map(chip => chip.id as AlarmCategory),
  );
  private categoryFilterActive = false;

  constructor(
    private router: Router,
    private translateService: TranslateService,
  ) {
    this.updateAlarmCategoryChips();
  }

ngOnChanges(changes: SimpleChanges): void {
  if (changes['popupVersion']) {
    this.resetPopupState();
  }

  if (changes['expandOnOpen']) {
    this.expanded = this.expandOnOpen;
  }

  if (changes['alarms'] || changes['popupVersion']) {
    this.updateAlarmCategoryChips();
  }
}

  get filteredAlarms(): any[] {
    return this.categoryFilterActive
      ? this.alarms.filter(alarm => this.selectedCategories.has(alarm?.alarmCategory as AlarmCategory))
      : this.alarms;
  }

  private updateAlarmCategoryChips(): void {
    this.alarmCategoryChips = this.alarmCategoryChipDefinitions.map(chip => ({
      ...chip,
      label: `${this.getAlarmCount(chip.id)} ${this.translateService.instant(chip.label)}`,
      selected: this.selectedCategories.has(chip.id as AlarmCategory),
    }));
  }

  private getAlarmCount(alarmCategory: string): number {
    return this.alarms.filter(alarm => alarm?.alarmCategory === alarmCategory).length;
  }

  getAlarmCategoryChip(alarmCategory: string): ChipItem | undefined {
    return this.alarmCategoryChips.find(chip => chip.id === alarmCategory);
  }

  getAlarmCategoryColor(alarmCategory: string): string | undefined {
    const statusCode = alarmCategoryStatusCodes[alarmCategory as AlarmCategory];
    return ColorStatusCodePipe.statuses.find(status => status.code === statusCode)?.color;
  }

  getAlarmCategoryLabel(alarmCategory: string): string | undefined {
    return this.alarmCategoryChipDefinitions.find(chip => chip.id === alarmCategory)?.label;
  }

  canOpenAlarm(alarm: any): boolean {
    return alarm?.turbine?.serialNav != null && !!alarm?._id_alarm;
  }

  openAlarm(alarm: any): void {
    const turbineId = alarm?.turbine?.serialNav;
    const alarmId = alarm?._id_alarm;

    this.clicked.emit();

    this.router.navigate(['/troubleshoot', 'turbine', turbineId, 'alarm', alarmId]);
  }

  private resetPopupState(): void {
    this.expanded = this.expandOnOpen;
    this.categoryFilterActive = false;

    this.selectedCategories = new Set<AlarmCategory>(
      this.alarmCategoryChipDefinitions.map(chip => chip.id as AlarmCategory),
    );
  }

  onAlarmCategoriesChange(selectedChips: ChipItem[]): void {
    this.categoryFilterActive = true;
    this.selectedCategories = new Set(selectedChips.map(chip => chip.id as AlarmCategory));
    this.updateAlarmCategoryChips();
  }

  onClick(): void {
    this.expanded = true;
    this.clicked.emit();
  }

  onChevronClick(): void {
    this.expanded = !this.expanded;

    if (this.expanded) {
      this.clicked.emit();
    }
  }

  onClose(): void {
    this.closed.emit();
  }
}
